<script setup lang="ts">
    import type { SearchHits, SearchHit } from '~/types/sammy/response';

    const props = defineProps<{
        searchHits?: SearchHits | null;
        query?: {
            q?: string;
        };
    }>();

    const { t } = useI18n();

    const filters = reactive({
        all: true,
        product: false,
        document: false,
        news: false,
        area: false
    });

    const container = ref<HTMLDivElement | null>(null);

    export type Filters = typeof filters;

    provide('filters', filters);

    const hasResults = computed(() => props.searchHits?.hits.length);

    const setMaxHeight = () => {
        const searchBar = document.querySelector('.search-wrapper');

        if (!searchBar || !container.value) return;

        requestAnimationFrame(() => {
            if (container.value) {
                container.value.style.maxHeight = `${document.documentElement.clientHeight - searchBar.getBoundingClientRect().bottom - 25}px`;
            }
        });
    };

    onMounted(() => {
        setMaxHeight();
        window.addEventListener('resize', setMaxHeight);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', setMaxHeight);
    });

    const isValidNewsHit = (hit: SearchHit) => hit._index === 'storyblok' && hit._source.storyblok_section === 'news';
    const isValidAreaHit = (hit: SearchHit) =>
        hit._index === 'storyblok' && hit._source.storyblok_section === 'fachkreis';

    const filteredResults = computed(() => {
        if (!props.searchHits) return [];

        return props.searchHits.hits.filter((hit) => {
            if (filters.all) return true;
            if (filters.product && hit._index === 'product') return true;
            if (filters.document && hit._index === 'document') return true;
            if (filters.news && isValidNewsHit(hit)) return true;
            if (filters.area && isValidAreaHit(hit)) return true;
            return false;
        });
    });
</script>

<template>
    <div
        ref="container"
        class="preview-container"
    >
        <div v-if="hasResults">
            <SearchPreviewFilter
                :product-has-results="props.searchHits?.hits.some((hit) => hit._index === 'product')"
                :document-has-results="props.searchHits?.hits.some((hit) => hit._index === 'document')"
                :news-has-results="props.searchHits?.hits.some(isValidNewsHit)"
                :area-has-results="props.searchHits?.hits.some(isValidAreaHit)"
            />
            <div class="results">
                <PreviewResult
                    v-for="result in filteredResults"
                    :key="result._id"
                    :result
                />
            </div>
        </div>

        <div v-else>
            <p v-if="query?.q && query.q.length > 2">{{ t('search.noResult') }} "{{ query.q }}"</p>
            <p v-else>
                {{ t('search.shortQuery') }}
            </p>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/search/search-result-preview.scss';
</style>
